import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Globe, Sparkles, Loader2, Copy, Check, Phone, Mail, MapPin, X } from "lucide-react";
import { toast } from "sonner";
import { StudioLayout } from "./StudioLayout";
import { EmptyState, ListSkeleton } from "@/components/studio/StudioUI";
import { studio, apiError, inputCls, btnPrimary, btnGhost } from "@/lib/studioApi";
import { mediaUrl } from "@/lib/photos";

const rel = (iso) => { const d = Math.round((Date.now() - new Date(iso)) / 36e5); return d < 1 ? "à l'instant" : d < 24 ? `il y a ${d} h` : `il y a ${Math.round(d / 24)} j`; };

export default function StudioWebRequests() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [settings, setSettings] = useState(null);
  const [slug, setSlug] = useState("");
  const [busy, setBusy] = useState("");
  const [adjust, setAdjust] = useState({});
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    studio.get("/web-requests").then(({ data }) => setItems(data.items)).catch(() => setItems([]));
    studio.get("/web-requests/settings").then(({ data }) => { setSettings(data); setSlug(data.slug); }).catch(() => {});
  }, []);

  const copy = async () => {
    try { await navigator.clipboard.writeText(settings.url); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { toast.message("Copie impossible : sélectionnez le lien", { description: settings.url }); }
  };
  const saveSlug = async () => {
    setBusy("slug");
    try { const { data } = await studio.put("/web-requests/settings", { slug }); setSettings(data); setSlug(data.slug); toast.success("Adresse mise à jour."); }
    catch (e) { toast.error(apiError(e, "Adresse indisponible.")); } finally { setBusy(""); }
  };
  const toQuote = async (r) => {
    setBusy(r.id);
    try {
      const { data } = await studio.post(`/web-requests/${r.id}/to-quote`, { price_adjust_percent: Number(adjust[r.id] || 0) }, { timeout: 120000 });
      toast.success(data.already ? "Devis déjà créé pour cette demande." : `Devis brouillon créé avec ${data.lines} lignes : ajustez les prix puis envoyez.`);
      navigate(`/studio/devis/${data.quote_id}`);
    } catch (e) { toast.error(apiError(e, "Création impossible.")); } finally { setBusy(""); }
  };
  const setStatus = async (r, status) => {
    try { await studio.put(`/web-requests/${r.id}`, { status }); setItems((l) => l.map((x) => (x.id === r.id ? { ...x, status } : x))); } catch (e) { toast.error(apiError(e)); }
  };

  const open = (items || []).filter((r) => r.status === "nouvelle");
  const done = (items || []).filter((r) => r.status !== "nouvelle");

  return (
    <StudioLayout title="Demandes web" subtitle="Les demandes de devis envoyées depuis votre site arrivent ici. Une touche : l'IA en fait un devis chiffré que vous ajustez.">
      <div data-testid="webrequest-settings" className="mt-6 rounded-2xl border border-[#C9D4F4] bg-[#F6F8FD] p-5">
        <h2 className="font-heading text-base font-semibold text-[#0F2645] inline-flex items-center gap-2"><Globe className="h-4 w-4 text-[#3A5CC8]" /> Connecter mon site web</h2>
        <p className="mt-1 text-xs text-[#5C6A87]">Faites pointer le bouton « Demander un devis » de votre site vers cette page Devisto à votre nom. Les demandes (avec photos) apparaissent ci-dessous et vous êtes notifié.</p>
        {settings && (
          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="flex flex-1 items-center gap-2 rounded-xl border border-[#E2E7F7] bg-white px-3 py-2">
              <span className="truncate text-xs text-[#93A0BC]">{settings.url.replace(/\/[^/]+$/, "/")}</span>
              <input data-testid="webrequest-slug" className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-[#0F2645] outline-none" value={slug} onChange={(e) => setSlug(e.target.value)} />
            </div>
            {slug !== settings.slug && <button type="button" data-testid="webrequest-slug-save" onClick={saveSlug} disabled={busy === "slug"} className={btnPrimary}>{busy === "slug" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Enregistrer</button>}
            <button type="button" data-testid="webrequest-copy" onClick={copy} className={btnGhost}>{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? "Copié" : "Copier le lien"}</button>
            <a data-testid="webrequest-preview" href={`/demande/${settings.slug}`} target="_blank" rel="noreferrer" className={btnGhost}>Voir la page</a>
          </div>
        )}
      </div>

      <p data-testid="webrequests-count" className="mt-6 text-sm text-[#5C6A87]">{`${open.length} nouvelle${open.length > 1 ? "s" : ""} demande${open.length > 1 ? "s" : ""}`}</p>
      <div data-testid="webrequests-list" className="mt-3 space-y-3">
        {items === null && <ListSkeleton />}
        {[...open, ...done].map((r) => (
          <div key={r.id} data-testid={`webrequest-${r.id}`} className={`rounded-2xl border bg-white p-4 sm:p-5 ${r.status === "nouvelle" ? "border-[#C9D4F4]" : "border-[#E2E7F7] opacity-80"}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="font-heading text-sm font-extrabold text-[#0F2645]">{r.name} <span className="ml-1 text-xs font-semibold text-[#93A0BC]">{rel(r.created_at)}</span></p>
                <p className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-[#5C6A87]">
                  {r.phone && <a href={`tel:${r.phone}`} className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{r.phone}</a>}
                  {r.email && <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{r.email}</a>}
                  {(r.address || r.city) && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{[r.address, r.postal_code, r.city].filter(Boolean).join(" ")}</span>}
                </p>
              </div>
              <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${r.status === "nouvelle" ? "bg-sky-50 text-sky-700 border-sky-200" : r.status === "traitee" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-[#5C6A87] border-[#E2E7F7]"}`}>{{ nouvelle: "Nouvelle", traitee: "Devis créé", refusee: "Sans suite" }[r.status]}</span>
            </div>
            <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-[#0F2645]">{r.description}</p>
            {r.media_ids?.length > 0 && <div className="mt-3 flex gap-2 overflow-x-auto">{r.media_ids.map((m) => <img key={m} src={mediaUrl(m)} alt="" className="h-20 w-20 shrink-0 rounded-lg object-cover" />)}</div>}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {r.quote_id ? (
                <button type="button" data-testid={`webrequest-open-quote-${r.id}`} onClick={() => navigate(`/studio/devis/${r.quote_id}`)} className={btnPrimary}>Ouvrir le devis</button>
              ) : (
                <>
                  <button type="button" data-testid={`webrequest-to-quote-${r.id}`} onClick={() => toQuote(r)} disabled={busy === r.id} className={btnPrimary}>
                    {busy === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Transformer en devis (IA)
                  </button>
                  <label className="inline-flex items-center gap-1.5 text-xs text-[#5C6A87]">Prix IA
                    <select data-testid={`webrequest-adjust-${r.id}`} className="rounded-lg border border-[#E2E7F7] bg-white px-2 py-1.5 text-xs font-semibold text-[#0F2645]" value={adjust[r.id] || "0"} onChange={(e) => setAdjust((a) => ({ ...a, [r.id]: e.target.value }))}>
                      {[-20, -10, 0, 10, 20, 30].map((v) => <option key={v} value={v}>{v > 0 ? `+${v}` : v} %</option>)}
                    </select>
                  </label>
                  {r.status === "nouvelle" && <button type="button" data-testid={`webrequest-dismiss-${r.id}`} onClick={() => setStatus(r, "refusee")} className={btnGhost}><X className="h-4 w-4" /> Sans suite</button>}
                </>
              )}
            </div>
          </div>
        ))}
        {items !== null && !items.length && <EmptyState testId="webrequests-empty" icon={Globe} title="Aucune demande pour l'instant" hint="Copiez votre lien ci-dessus et placez-le sur votre site, vos réseaux ou votre fiche Google." />}
      </div>
    </StudioLayout>
  );
}
